import { createRoot } from 'react-dom/client';
import { MemoryRouter } from 'react-router';
import { Provider } from 'react-redux';

import '@/localization/i18next';
import config from '@/config';
import { store } from '@/store';

import GameWrapper from '@/features/gameSession/components/GameWrapper';
import ErrorBoundary from '@/shared/components/errorBoundary/ErrorBoundary';
import ErrorHandler from '@/shared/components/errorHandler/ErrorHandler';
import Notification from '@/shared/components/notification/Notification';
import ImagePreviewModal from '@/shared/modules/imagePreview/components/ImagePreviewModal';

import '@/assets/styles/main.scss';

import type { Root } from 'react-dom/client';

type EmbedTarget = HTMLElement | string;

type FwWebGamesEmbedApi = {
	mount: (target?: EmbedTarget) => void;
	unmount: () => void;
};

declare global {
	interface Window {
		FwWebGamesEmbed?: FwWebGamesEmbedApi;
		__FW_WEB_GAMES_EMBED_AUTO_MOUNTED__?: boolean;
	}
}

const DEFAULT_ROOT_ID = 'web-games-root';

let activeRoot: Root | null = null;

const getInitialRouterEntry = () => {
	return `${window.location.pathname}${window.location.search}${window.location.hash}`;
};

const EmbeddedGameApp = () => {
	return (
		<Provider store={store}>
			<MemoryRouter initialEntries={[getInitialRouterEntry()]}>
				<ErrorBoundary>
					<ErrorHandler errorType="page-blocking">
						<GameWrapper />
					</ErrorHandler>
					<Notification />
					<ImagePreviewModal />
				</ErrorBoundary>
			</MemoryRouter>
		</Provider>
	);
};

const getConfiguredRootId = () => {
	return config.container_id || config.mount_id || DEFAULT_ROOT_ID;
};

const resolveTargetElement = (target?: EmbedTarget) => {
	if (target instanceof HTMLElement) {
		return target;
	}

	if (typeof target === 'string') {
		const selectorTarget = target.startsWith('#') || target.startsWith('.') ? document.querySelector(target) : null;

		if (selectorTarget instanceof HTMLElement) {
			return selectorTarget;
		}

		const idTarget = document.getElementById(target.replace(/^#/, ''));

		if (idTarget) {
			return idTarget;
		}
	}

	const configuredRootId = getConfiguredRootId();
	const configuredRoot = document.getElementById(configuredRootId);

	if (configuredRoot) {
		return configuredRoot;
	}

	const defaultRoot = document.getElementById('root');

	if (defaultRoot) {
		return defaultRoot;
	}

	const container = document.createElement('div');
	container.id = configuredRootId;
	document.body.appendChild(container);

	return container;
};

export const mount = (target?: EmbedTarget) => {
	const targetElement = resolveTargetElement(target);

	if (activeRoot) {
		activeRoot.unmount();
	}

	activeRoot = createRoot(targetElement);
	activeRoot.render(<EmbeddedGameApp />);
};

export const unmount = () => {
	if (!activeRoot) {
		return;
	}

	activeRoot.unmount();
	activeRoot = null;
};

const autoMount = () => {
	if (window.__FW_WEB_GAMES_EMBED_AUTO_MOUNTED__) {
		return;
	}

	window.__FW_WEB_GAMES_EMBED_AUTO_MOUNTED__ = true;
	mount();
};

const runWhenDomIsReady = (callback: () => void) => {
	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', callback, { once: true });
		return;
	}

	callback();
};

window.FwWebGamesEmbed = {
	mount,
	unmount,
};

runWhenDomIsReady(autoMount);
